import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/Primitives/Carousel"
import { Button } from "@/components/Primitives/Button"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { AuthLogo } from "@/components/Primitives"
import { useConfig } from "@/hooks/useConfig"
import { useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { Icon } from "@/components/Primitives/Icon"
import { cn } from "@/lib/utils/utils"

const slideImages = [
  "/images/purple-checkmark.webp",
  "/images/phone-map.webp",
  "/images/family-in-couch.webp",
]
const contentKeys = [
  "config_onboarding_slide_1_content",
  "config_onboarding_slide_2_content",
  "config_onboarding_slide_3_content",
]

function useOnboardingCarousel() {
  const navigate = useNavigate()
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  const handleNext = () => {
    if (!api) return

    if (current < count - 1) {
      api.scrollNext()
    } else {
      navigate("/quests")
    }
  }

  const handlePrev = () => {
    if (!api) return
    api.scrollPrev()
  }

  const isLastSlide = current === count - 1
  const canScrollPrev = api?.canScrollPrev() ?? false

  return {
    api,
    setApi,
    current,
    count,
    handleNext,
    handlePrev,
    isLastSlide,
    canScrollPrev,
  }
}

function Stepper({
  className,
  current,
}: {
  className?: string
  current: number
}) {
  return (
    <div className={cn("flex gap-2", className)}>
      {Array.from({ length: slideImages.length }, (_, index) => (
        <div
          key={index}
          className={`h-2 w-3 bg-purple-200 rounded-full transition-all duration-500 ease-in-out ${
            index === current ? "bg-purple-500 w-9" : index < current
          }`}
        />
      ))}
    </div>
  )
}

export function SignupOnboardingPage() {
  const { t } = useConfig()
  const {
    setApi,
    current,
    handleNext,
    handlePrev,
    isLastSlide,
    canScrollPrev,
  } = useOnboardingCarousel()

  return (
    <div className="flex flex-col items-center justify-center w-full min-h-[calc(100dvh-2rem)] relative">
      <AuthLogo />
      <div className="w-full pt-4 md:px-12">
        <Stepper className="md:hidden" current={current} />
        <Carousel
          setApi={setApi}
          opts={{
            loop: false,
            dragFree: false,
            containScroll: "trimSnaps",
            align: "start",
            duration: 10,
            watchDrag: false,
          }}
          className="w-full"
        >
          <CarouselContent>
            {slideImages.map((img, idx) => (
              <CarouselItem key={idx}>
                <div className="flex flex-col justify-between h-[calc(100dvh-6rem)] md:flex-row-reverse md:items-center md:gap-10 md:h-[calc(100dvh-12rem)]">
                  <div className="flex flex-col items-start md:w-1/2">
                    <Stepper className="hidden md:flex" current={current} />
                    <MarkdownRenderer
                      content={t(contentKeys[idx])}
                      className="text-neutral-50 font-light"
                    />
                  </div>

                  <div className="flex flex-col items-center md:w-1/2">
                    <img
                      src={img}
                      alt=""
                      className="w-full object-contain object-top max-h-[40vh] md:max-h-[65vh]"
                    />
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <div className="hidden md:block absolute bottom-30 right-12 z-10">
          <div className="flex justify-end gap-2">
            {canScrollPrev ? (
              <Button onClick={handlePrev} variant="outline">
                <Icon icon={ArrowLeft} className="text-neutral-900" />
              </Button>
            ) : (
              <div />
            )}
            <Button onClick={handleNext}>
              {isLastSlide
                ? t("config_onboarding_button_finish")
                : t("config_onboarding_button_continue")}
              <Icon icon={ArrowRight} className="text-neutral-50" />
            </Button>
          </div>
        </div>

        {/* Mobile botons */}
        <div className="flex md:hidden justify-end gap-2 w-full">
          {canScrollPrev ? (
            <Button onClick={handlePrev} variant="outline">
              <Icon icon={ArrowLeft} className="text-neutral-900" />
            </Button>
          ) : (
            <div />
          )}
          <Button onClick={handleNext}>
            {isLastSlide
              ? t("config_onboarding_button_finish")
              : t("config_onboarding_button_continue")}
            <Icon icon={ArrowRight} className="text-neutral-50" />
          </Button>
        </div>
      </div>
    </div>
  )
}
